/*
 * @Description:
 * @Author: Jack Deng
 * @Date: 2021-04-12 10:58:25
 * @LastEditors: Json Chen
 * @LastEditTime: 2022-05-27 16:09:08
 * @FilePath: /unifyxp-v2-store-portal/src/pages/FileLibrary/Components/FileList/index.tsx
 */
import { BulkEditor, Filter } from '@/src/components';
import { Button, SizeFittingTable, Tag, Upload } from '@/src/components/Antd';
import Warning from '@/src/components/Antd/Modal/WarningModal';
import Sort from '@/src/constrains/Query/Sort';
import { UPLOAD_VIDEO } from '@/src/tools/constant';
import { getFileExt, transSizeToKB } from '@/src/tools/helper';
import { Icon } from '@/src/tools/icon';
import { msg } from '@/src/tools/message';
import { MediaFile } from '@/src/types/common/media';
import { Popover, Progress, Row, Tabs, Tooltip } from 'antd';
import _ from 'lodash';
import React, { useState } from 'react';
import Pagination from 'src/components/StyledPagination/GlobalPagination';
import TagGroup from 'src/components/TagGroup/TagGroup';
import { defaultPageSize } from 'src/tools/constant';
import Hint from '../Hint';
import { Card } from '../index';
import LayoutSwitch from '../LayoutSwtich';
import { filterConfig, sortConfig } from './config';
import styles from './style.module.less';
import { useI18n } from 'src/hooks/useRouterChange';
import {
  i18nGet,
  replaceI18nVariables,
  handleOnGetStoreTimezoneUTC,
  handleOnFormatStoreTimeAndDateWithTimezone,
} from 'src/tools/tools';

type Props = {
  canUpload: boolean;
  canDelete: boolean;
  total: number;
  files: Array<any>;
  tagList: Array<any>;
  onSearch: (obj: Record<string, any>) => void;
  onDelete?: (productIds: Array<string>) => void;
  onNavigateToFileDetail: (uuid: string) => void;
  onChangeFileList: ({ file, fileList, event }) => void;
};

function FileList({
  canUpload,
  canDelete,
  total,
  tagList,
  onSearch,
  files,
  onDelete,
  onNavigateToFileDetail,
  onChangeFileList,
}: Props) {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const [visible, setVisible] = useState(0);
  const [aboutToDelete, setAboutToDelete] = useState([]);
  const [layout, setLayout] = useState<'grid' | 'list'>('list');
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [type, setTypes] = useState('');
  const [selectedTag, setSelectedTag] = useState<any>();
  const [selectedFiles, setSelectFiles] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [sort, setSort] = useState({
    name: getI18n('storeFileLibrary.list.uploadednewest'),
    key: 'modifiedDate 0',
  });

  const handleValueOnChange = (value) => setSearchValue(value);

  const handleSearch = () => {
    if (onSearch) {
      onSearch(getParams());
    }
  };

  const handleClickFilter = (item, list) => {
    const params = getParams();
    if (item.name === 'Type') {
      const type =
        Array.isArray(list) && list.length !== 0 ? list[0].key : null;
      setTypes(type);
      if (onSearch) {
        onSearch({
          ...params,
          startIndex: 0,
          body: {
            ...params.body,
            type: type || null,
          },
        });
      }
    }
    if (item.name === 'Tags') {
      const result = Array.isArray(list) && list.length !== 0 ? list[0] : null;
      setSelectedTag(result);
      if (onSearch) {
        onSearch({
          ...params,
          startIndex: 0,
          body: {
            ...params.body,
            tag: { uuid: result.key, name: result.name },
          },
        });
      }
    }
  };

  const handleDeleteFiles = (selectedRowKeys) => {
    onDelete(selectedRowKeys);
    setSelectFiles([]);
    hideWarning();
  };

  const handleClickSort = (list) => {
    const params = getParams();
    setSort(list);
    const [field, ascent] = list.key.split(' ');
    if (onSearch) {
      onSearch({
        ...params,
        body: {
          ...params.body,
          sortBy: {
            field,
            ascent: ascent === '1',
          },
        },
      });
    }
  };

  const handleSelectChange = (selectedRowKeys) => {
    setSelectFiles(selectedRowKeys.filter((v) => v));
  };

  const handleSelectFiles = (uuid: string) => {
    const result = _.cloneDeep(selectedFiles);
    const index = result.indexOf(uuid);
    if (index !== -1) {
      result.splice(index, 1);
    } else {
      result.push(uuid);
    }
    setSelectFiles(result);
  };

  const handleDeleteType = () => {
    const params = getParams();
    setTypes('');
    if (onSearch) {
      onSearch({
        ...params,
        body: {
          ...params.body,
          type: null,
        },
      });
    }
  };

  const handleDeleteTags = () => {
    const params = getParams();
    setSelectedTag(null);
    if (onSearch) {
      onSearch({
        ...params,
        body: {
          ...params.body,
          tag: null,
        },
      });
    }
  };

  const handlePageChange = (page) => {
    const params = getParams();
    setCurrent(page);
    if (onSearch) {
      onSearch({
        ...params,
        startIndex: (page - 1) * pageSize,
      });
    }
  };

  const handlePageSizeChange = (e) => {
    const params = getParams();
    const size = e.value;
    setPageSize(size);
    if (onSearch) {
      onSearch({
        ...params,
        startIndex: (current - 1) * size,
        maxCount: size,
      });
    }
  };

  const getParams = () => {
    const [field, ascent] = sort.key.split(' ');
    const req = {
      type: type ? type : null,
      searchText: searchValue ? [searchValue] : null,
      tag: selectedTag
        ? { uuid: selectedTag.key, name: selectedTag.name }
        : null,
      sortBy: {
        field,
        ascent: ascent === '1',
      },
    };
    return {
      body: req,
      startIndex: (current - 1) * pageSize,
      maxCount: pageSize,
    };
  };

  const showWarning = (uuids: string[], e?: any) => {
    if (e) {
      e.stopPropagation();
    }
    if (canDelete) {
      if (uuids.length === 0) {
        msg.error('No file selected!');
      } else {
        setVisible(uuids.length === 1 ? 1 : 2);
        setAboutToDelete(uuids);
      }
    }
  };

  const hideWarning = () => {
    setVisible(0);
    setAboutToDelete([]);
  };

  const columns = [
    {
      title: getI18n('storeFileLibrary.detail.fileName'),
      key: 'file',
      default: true,
      minWidth: 481,
      childGenerate: (_, record, index) => {
        const ext = getFileExt(record.originalName);
        return (
          <div className={styles.products}>
            {record.url ? (
              <div
                className={styles.img}
                style={{ backgroundImage: `url('${record.url}')` }}>
                {UPLOAD_VIDEO.indexOf(ext) !== -1 && (
                  <Icon type='iconvideo' className={styles.video} />
                )}
              </div>
            ) : (
              <div className={styles.img}>
                <Progress
                  type='circle'
                  percent={record.percent}
                  showInfo={false}
                  width={20}
                />
              </div>
            )}
            <Tooltip placement={'topLeft'} title={record.originalName}>
              <span id={'fileName'} className={styles.name}>
                {record.percent
                  ? getI18n('storeFileLibrary.list.loading')
                  : record.originalName || ''}
              </span>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: getI18n('storeFileLibrary.list.size'),
      dataIndex: 'size',
      key: 'size',
      default: true,
      width: 103,
      childGenerate: (text: number) => {
        return transSizeToKB(text);
      },
    },
    {
      title: `${getI18n(
        'storeFileLibrary.detail.uploadedat',
      )} (UTC ${handleOnGetStoreTimezoneUTC()})`,
      key: 'product',
      dataIndex: 'createdDate',
      default: true,
      width: 172,
      childGenerate: (text) => {
        const t = handleOnFormatStoreTimeAndDateWithTimezone(text);
        return t;
      },
    },
    {
      title: getI18n('general.tags'),
      dataIndex: 'tags',
      default: true,
      optional: true,
      align: 'center',
      minWidth: 288,
      tags: true,
      tagsGenerator: (
        characterLength,
        width,
        marginPaddingWidth,
        containPaddingWidth,
        tags,
        record,
      ) => {
        return (
          <TagGroup
            characterLength={characterLength}
            containerWidth={width}
            marginPaddingWidth={marginPaddingWidth}
            containPaddingWidth={containPaddingWidth}
            tags={tags}
          />
        );
      },
    },
    {
      title: getI18n(
        'general.advancedFilters.commonStoreProductsWithTable.actions',
      ),
      width: 72,
      default: true,
      childGenerate: (text, record) => {
        return (
          <span
            className={styles.delete}
            style={
              !canDelete
                ? {
                    color: '#ccc',
                  }
                : {}
            }
            onClick={showWarning.bind(this, [record.uuid])}>
            {getI18n('general.delete')}
          </span>
        );
      },
    },
  ];
  return (
    <div className={styles.container}>
      <div className={styles.title}>
        <span className={styles.titleLeft}>
          <span>{getI18n('settings.list.fileLibrary')}</span>
          <Popover title={null} content={Hint} placement='right'>
            <Icon type='icontooltip' className={styles.toolTipIcon} />
          </Popover>
        </span>
        <Upload
          accept={'image/*,video/*'}
          drag={false}
          multiple
          fileList={files}
          onChangeStatus={onChangeFileList}
          showUploadList={false}
          disabled={!canUpload}>
          <Button type='primary' disabled={!canUpload}>
            {getI18n('general.antd.Uploader.upload_files')}
          </Button>
        </Upload>
      </div>
      <div className={styles.tabs}>
        <Tabs tabBarStyle={{ height: '56px' }}>
          <Tabs.TabPane
            tab={<div className={styles.tabPane}>{getI18n('general.all')}</div>}
            key='all'></Tabs.TabPane>
        </Tabs>
      </div>
      <div className={styles.action}>
        <Filter
          searchValue={searchValue}
          searchOnchange={handleValueOnChange}
          pressEnter={handleSearch}
          onSearch={handleSearch}
          overlayClick={handleClickFilter}
          filterList={filterConfig(tagList, { getI18n })}
        />
        <div className={styles.actionRight}>
          <Sort
            sortList={sortConfig({ getI18n })}
            name={sort.name}
            sortClick={handleClickSort}
            sortKey={sort.key}
          />
        </div>
      </div>
      <div className={styles.filters}>
        {type && (
          <Tag closable onClose={handleDeleteType}>
            {type}
          </Tag>
        )}
        {selectedTag && (
          <Tag key={selectedTag.key} closable onClose={handleDeleteTags}>
            {selectedTag.name}
          </Tag>
        )}
      </div>
      <div className={styles.editor}>
        <BulkEditor
          buttonMenu={[
            {
              key: 'delete',
              value: getI18n('general.delete'),
              function: showWarning,
              disabled: !canDelete,
            },
          ]}
          selectedRowKeys={selectedFiles}
          hideSelectAll
          hideSelectDescription
        />
        <LayoutSwitch
          value={layout}
          onChange={(value: any) => {
            setSelectFiles([]);
            setLayout(value);
          }}
        />
      </div>
      <div className={styles.table}>
        {layout === 'list' && (
          <SizeFittingTable
            rowKey='uuid'
            pagination={false}
            id='salesChannelProductList'
            dataSource={files}
            columns={columns}
            rowClassName={styles.row}
            onRow={(record: MediaFile) => ({
              onClick: onNavigateToFileDetail.bind(this, record.uuid),
            })}
            rowSelection={{
              onChange: handleSelectChange,
            }}
          />
        )}
        {layout === 'grid' && Array.isArray(files) && files.length !== 0 && (
          <Row gutter={[16, 16]}>
            {files.map((file: MediaFile) => {
              return (
                <Card
                  loading={file.status === 'uploading'}
                  percent={file.percent}
                  key={file.uuid || file.uid}
                  file={file}
                  checked={selectedFiles.indexOf(file.uuid) !== -1}
                  onNavigateToDetail={onNavigateToFileDetail}
                  onDelete={showWarning}
                  onClick={handleSelectFiles}
                />
              );
            })}
          </Row>
        )}
        <Pagination
          current={current}
          total={total}
          pageSize={pageSize}
          pageOnChange={handlePageChange}
          pageSizeChange={handlePageSizeChange}
          paginationProps={{ defaultPageSize: pageSize }}
        />
      </div>

      <Warning
        visible={Boolean(visible)}
        header={
          visible === 1
            ? getI18n('storeFileLibrary.detail.deletefile')
            : replaceI18nVariables(
                getI18n('storeFileLibrary.detail.deletefiles'),
                aboutToDelete.length,
              )
        }
        onOk={handleDeleteFiles.bind(this, aboutToDelete)}
        okText={getI18n('general.delete')}
        cancelText={getI18n('general.cancel')}
        onCancel={hideWarning}>
        {visible === 1
          ? getI18n('storeFileLibrary.detail.deletefilewarning1')
          : getI18n('storeFileLibrary.detail.deletefilewarning2')}
      </Warning>
    </div>
  );
}

export default FileList;
